import type { MatchingExerciseBlock, SourceLanguageCode } from '@ellie/shared';
import { BlockHeader } from '../BlockHeader';
import { NativeHint } from '../NativeHint';

interface Props {
  block: MatchingExerciseBlock;
  index: number;
  nativeLanguage: SourceLanguageCode;
  targetLanguage: string;
  pageNumber: number;
}

/**
 * Renders column B items in a shuffled order so the preview
 * doesn't reveal the answers. Uses a deterministic shuffle
 * based on the block id.
 */
function shuffleColumnB(items: string[]): string[] {
  // Simple copy + reverse for a consistent non-answer-revealing order
  return [...items].reverse();
}

export function MatchingExercisePreview({
  block,
  index,
  nativeLanguage,
  targetLanguage,
  pageNumber,
}: Props) {
  const shuffledB = shuffleColumnB(block.columnB);

  return (
    <div className="wb-page">
      <BlockHeader block={block} index={index} />

      <div className="wb-block-instructions">
        <span className="wb-icon">{'\u{1F517}'}</span>
        <div>
          <p>{block.instructions}</p>
          <NativeHint
            text={block.instructionsNative}
            nativeLanguage={nativeLanguage}
            label={targetLanguage.toUpperCase()}
          />
        </div>
      </div>

      <div className="wb-matching-columns">
        {/* Left column */}
        <div className="wb-match-column">
          <div className="wb-match-column-header">A</div>
          {block.columnA.map((item, i) => (
            <div key={i} className="wb-match-item wb-left">
              <span className="wb-num">{i + 1}</span>
              {item}
            </div>
          ))}
        </div>

        {/* Connector arrows */}
        <div className="wb-match-connector">
          {block.columnA.map((_, i) => (
            <div key={i} className="wb-match-answer-box">
              &rarr;
            </div>
          ))}
        </div>

        {/* Right column (shuffled) */}
        <div className="wb-match-column">
          <div className="wb-match-column-header">B</div>
          {shuffledB.map((item, i) => (
            <div key={i} className="wb-match-item wb-right">
              <span className="wb-letter">{String.fromCharCode(97 + i)}</span>
              {item}
            </div>
          ))}
        </div>
      </div>

      <span className="wb-page-number">{pageNumber}</span>
    </div>
  );
}
